import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import AddIcon from '@mui/icons-material/Add';

import {
  defaultEmptySelectedOption,
  defaultSelectedDataOption,
  ISelectedDataOption,
  ISelectedOption,
  TP_COLORS,
} from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { getAuthState } from '../../../store/AuthSlice';
import {
  adminDeleteSubTopic,
  adminGetSubTopicList,
  getSubTopicState,
  adminLoadMoreSubTopicList,
  ISubTopic,
  resetSubTopicList,
} from '../../../store/SubTopicSlice';
import { getTopics, getTopicState } from '../../../store/TopicSlice';
import {
  getCurriculumState,
  getSubjectMapDataBySubjectCode,
  setSelectedValues,
} from '../../../store/CurriculumSlice';
import {
  checkUserPermission,
  constructOptionsFromArray,
  getSubjectColor,
} from '../../../helper';
import ImageContainer from './components/ImageContainer';
import SubTopicListItem from './components/SubTopicListItem';
import SimpleConfirmationAlert from '../../../components/Alert/SimpleConfirmationAlert';
import ViewLoggerDrawer from './modals/ViewLoggerDrawer';
import AddNewSubTopicDrawer from './modals/AddNewSubTopicDrawer';
import UpdateSubTopicDrawer from './modals/UpdateSubTopicDrawer';
import EditTopicModal from './modals/EditTopicModal';
import FullScreenLoader from '../../../components/Loaders/FullScreenloader';

interface IFilterData {
  limit: number;
  offset: number;
}

interface IState {
  selectedSubTopic?: ISubTopic;
  filterData: IFilterData;
  isViewLoggerDrawerOpen: boolean;
  isDeleteSubTopicConfirmationAlertOpen: boolean;
  isAddSubTopicDrawerOpen: boolean;
  isUpdateSubTopicDrawerOpen: boolean;
  isUpdateTopicSortingOrderModalOpen: boolean;
}

const defaultFilterData: IFilterData = {
  limit: 30,
  offset: 0,
};

const initialState: IState = {
  selectedSubTopic: undefined,
  filterData: defaultFilterData,
  isViewLoggerDrawerOpen: false,
  isDeleteSubTopicConfirmationAlertOpen: false,
  isAddSubTopicDrawerOpen: false,
  isUpdateSubTopicDrawerOpen: false,
  isUpdateTopicSortingOrderModalOpen: false,
};

const SubTopics: React.FC = () => {
  const urlParams = useParams();
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const topicState = useAppSelector(getTopicState);
  const subTopicState = useAppSelector(getSubTopicState);
  const curriculumState = useAppSelector(getCurriculumState);

  const subjectCode = urlParams.subjectCode || '';
  const subjectMapData = curriculumState.subjectMapDataForSubjectCode;

  let subject = '';
  let curriculum = '';
  let grade = '';

  if (subjectMapData) {
    subject = subjectMapData.subject;
    curriculum = subjectMapData.curriculum;
    grade = subjectMapData.grade;
  }

  // Permissions
  const subTopicCreatePermission = checkUserPermission(authState.user, {
    adminSubTopic: ['create', 'edit', 'delete'],
  });

  const subTopicEditPermission = checkUserPermission(authState.user, {
    adminSubTopic: ['edit'],
  });

  const subTopicDeletePermission = checkUserPermission(authState.user, {
    adminSubTopic: ['delete'],
  });

  // State
  const [state, setState] = useState<IState>(initialState);
  const [subjectLevelData, setSubjectLevelData] = useState<ISelectedDataOption>(
    { ...defaultSelectedDataOption }
  );

  const [topicData, setTopicData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  useEffect(() => {
    if (subjectCode) {
      dispatch(getSubjectMapDataBySubjectCode({ subjectCode }));
    }
  }, []);

  useEffect(() => {
    if (
      subjectMapData &&
      subjectMapData.subjectLevels &&
      subjectMapData.subjectLevels.length
    ) {
      const newSubjectLevelData: ISelectedDataOption = {
        ...subjectLevelData,
        selectedItem: defaultEmptySelectedOption,
      };

      const newTopicData: ISelectedDataOption = {
        ...defaultSelectedDataOption,
      };

      newSubjectLevelData.options = constructOptionsFromArray(
        subjectMapData.subjectLevels,
        'name',
        'name',
        ['subjectLevels']
      );

      if (curriculumState?.selectedValues?.subjectLevel) {
        newSubjectLevelData.selectedItem =
          curriculumState.selectedValues.subjectLevel;
      } else if (newSubjectLevelData.options.length) {
        // eslint-disable-next-line prefer-destructuring
        newSubjectLevelData.selectedItem = newSubjectLevelData.options[0];
      }

      dispatch(
        getTopics({
          subjectCode,
          subjectLevel: newSubjectLevelData.selectedItem.label,
        })
      );

      setSubjectLevelData(newSubjectLevelData);
      setTopicData(newTopicData);
    }
  }, [subjectMapData]);

  useEffect(() => {
    if (topicState.list && topicState.list.length) {
      const newState: IState = {
        ...state,
        filterData: initialState.filterData,
      };

      const newTopicData: ISelectedDataOption = {
        ...topicData,
        selectedItem: defaultEmptySelectedOption,
      };

      newTopicData.options = _.sortBy(
        [
          ...constructOptionsFromArray(topicState.list, 'name', '_id', [
            'sortingOrder',
          ]),
        ],
        ['sortingOrder', 'label']
      );

      if (curriculumState?.selectedValues?.topic) {
        newTopicData.selectedItem = curriculumState.selectedValues.topic;
      } else if (newTopicData.options.length) {
        // eslint-disable-next-line prefer-destructuring
        newTopicData.selectedItem = newTopicData.options[0];
      }

      dispatch(
        adminGetSubTopicList({
          ...newState.filterData,
          topicId: newTopicData.selectedItem.value,
        })
      );

      setState(newState);
      setTopicData(newTopicData);
    }
  }, [topicState.list]);

  const handleSubjectLevelChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newSubjectLevelData: ISelectedDataOption = {
      ...subjectLevelData,
      selectedItem: value,
    };

    const newTopicData: ISelectedDataOption = {
      ...defaultSelectedDataOption,
    };

    dispatch(resetSubTopicList());

    dispatch(
      getTopics({
        subjectCode,
        subjectLevel: value.value,
      })
    );

    setSubjectLevelData(newSubjectLevelData);
    setTopicData(newTopicData);

    dispatch(
      setSelectedValues({
        subjectLevel: value,
        topic: undefined,
      })
    );
  };

  const handleTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newState: IState = {
      ...state,
      filterData: initialState.filterData,
    };

    const newTopicData: ISelectedDataOption = {
      ...topicData,
      selectedItem: value,
    };

    dispatch(
      adminGetSubTopicList({
        ...state.filterData,
        topicId: value.value,
      })
    );

    setTopicData(newTopicData);
    setState(newState);

    dispatch(
      setSelectedValues({
        topic: value,
      })
    );
  };

  const handleLoadMore = () => {
    const newState: IState = {
      ...state,
      filterData: {
        ...state.filterData,
        offset: subTopicState.list.length,
      },
    };

    dispatch(
      adminLoadMoreSubTopicList({
        ...newState.filterData,
        topicId: topicData.selectedItem.value,
      })
    );

    setState(newState);
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      placeholder="Select"
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {topicState.status === 'loading' ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              params.InputProps.endAdornment
            )}
          </>
        ),
      }}
    />
  );

  if (curriculumState.status === 'loading') {
    return <FullScreenLoader />;
  }

  return (
    <div>
      <ImageContainer
        subject={subject}
        curriculum={curriculum}
        grade={grade}
        subjectCode={subjectCode}
      />
      <Box style={{ padding: '0 30px' }}>
        <Grid container spacing={3} sx={{ mb: '30px' }}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography>Subject Level</Typography>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              value={subjectLevelData.selectedItem}
              options={subjectLevelData.options}
              renderInput={renderInput}
              loading={topicState.status === 'loading'}
              onChange={handleSubjectLevelChange}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography>Topic</Typography>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              onChange={handleTopicChange}
              options={topicData.options}
              value={topicData.selectedItem}
              renderInput={renderInput}
              loading={topicState.status === 'loading'}
              disabled={
                !subjectLevelData.selectedItem ||
                subjectLevelData.selectedItem === defaultEmptySelectedOption
              }
            />
          </Grid>
        </Grid>
        {subTopicCreatePermission && (
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mb: '10px' }}
          >
            <Button
              color="inherit"
              size="large"
              onClick={() =>
                setTimeout(() => {
                  setState((prevState) => ({
                    ...prevState,
                    isAddSubTopicDrawerOpen: true,
                  }));
                }, 250)
              }
              startIcon={
                <AddIcon
                  fontSize="large"
                  sx={{
                    color: TP_COLORS.white,
                    background: getSubjectColor(subject),
                    borderRadius: '100%',
                    width: '35px',
                    height: '35px',
                  }}
                />
              }
            >
              Add Sub Topic
            </Button>
            <Button
              size="small"
              color="inherit"
              variant="contained"
              disabled={topicData.options.length === 0}
              style={{
                color: TP_COLORS.black,
                maxHeight: '40px',
                marginLeft: '10px',
              }}
              onClick={() =>
                setState((prevState) => ({
                  ...prevState,
                  isUpdateTopicSortingOrderModalOpen: true,
                }))
              }
            >
              Edit Topics
            </Button>
          </Stack>
        )}
        <div style={{ paddingBottom: '15px' }}>
          {subTopicState.list.length > 0 &&
            subTopicState.list.map((subTopic, index) => {
              return (
                <SubTopicListItem
                  key={index}
                  isEditable={subTopicEditPermission}
                  isDeletable={subTopicDeletePermission}
                  subTopic={subTopic}
                  toggleViewLogger={(selectedItem) => {
                    setState((prevState) => ({
                      ...prevState,
                      selectedSubTopic: selectedItem,
                      isViewLoggerDrawerOpen: true,
                    }));
                  }}
                  handleDelete={(selectedItem) => {
                    setState((prevState) => ({
                      ...prevState,
                      selectedSubTopic: selectedItem,
                      isDeleteSubTopicConfirmationAlertOpen: true,
                    }));
                  }}
                  handleUpdate={(selectedItem) => {
                    setState((prevState) => ({
                      ...prevState,
                      selectedSubTopic: selectedItem,
                      isUpdateSubTopicDrawerOpen: true,
                    }));
                  }}
                />
              );
            })}
          {subTopicState.count > subTopicState.list.length && (
            <Stack direction="row" justifyContent="center">
              <Button
                disabled={topicState.status === 'loading'}
                onClick={handleLoadMore}
              >
                Load More
              </Button>
            </Stack>
          )}
        </div>
      </Box>

      {/* MODALS */}
      <ViewLoggerDrawer
        open={state.isViewLoggerDrawerOpen}
        subTopic={state.selectedSubTopic}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            selectedSubTopic: undefined,
            isViewLoggerDrawerOpen: false,
          }));
        }}
      />

      {state.isAddSubTopicDrawerOpen && (
        <AddNewSubTopicDrawer
          open={state.isAddSubTopicDrawerOpen}
          onClose={() =>
            setState((prevState) => ({
              ...prevState,
              isAddSubTopicDrawerOpen: false,
            }))
          }
          data={{
            subjectCode,
            subject,
            curriculum,
            grade,
            topic: topicData.selectedItem.label,
            topicId: topicData.selectedItem.value,
          }}
        />
      )}

      {state.isUpdateSubTopicDrawerOpen && state.selectedSubTopic && (
        <UpdateSubTopicDrawer
          open={state.isUpdateSubTopicDrawerOpen}
          subTopic={state.selectedSubTopic}
          onClose={() => {
            setState((prevState) => ({
              ...prevState,
              selectedSubTopic: undefined,
              isUpdateSubTopicDrawerOpen: false,
            }));
          }}
        />
      )}

      {state.isUpdateTopicSortingOrderModalOpen && (
        <EditTopicModal
          open={state.isUpdateTopicSortingOrderModalOpen}
          topics={topicData.options || []}
          subjectLevel={subjectLevelData.selectedItem.value}
          subject={subject}
          curriculum={curriculum}
          grade={grade}
          subjectCode={subjectCode}
          onClose={() =>
            setState((prevState) => ({
              ...prevState,
              isUpdateTopicSortingOrderModalOpen: false,
            }))
          }
        />
      )}

      <SimpleConfirmationAlert
        open={state.isDeleteSubTopicConfirmationAlertOpen}
        onClose={() => {
          setState((prevState) => ({
            ...prevState,
            selectedSubTopic: undefined,
            isDeleteSubTopicConfirmationAlertOpen: false,
          }));
        }}
        title="Are you sure"
        description="Do you want to delete this sub topic?"
        cancelButtonProps={{
          label: 'No',
          onClick: () => {
            setState((prevState) => ({
              ...prevState,
              selectedSubTopic: undefined,
              isDeleteSubTopicConfirmationAlertOpen: false,
            }));
          },
        }}
        okButtonprops={{
          label: 'Yes',
          color: 'error',
          onClick: () => {
            if (state.selectedSubTopic && state.selectedSubTopic._id) {
              dispatch(
                adminDeleteSubTopic({
                  id: state.selectedSubTopic._id,
                })
              );
            }

            setState((prevState) => ({
              ...prevState,
              selectedSubTopic: undefined,
              isDeleteSubTopicConfirmationAlertOpen: false,
            }));
          },
        }}
      />
    </div>
  );
};

export default SubTopics;
